import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Bell, Bot, ClipboardCheck, FolderOpen, LayoutDashboard, Laptop, Link2, Menu, Search, Shield, Trash2, Truck, Users } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useFiles } from "@/context/FilesContext";
import { api, fmtDate, ROLE_LABELS } from "@/lib/api";
import { Logo } from "@/pages/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CompanySwitcher } from "@/components/layout/CompanySwitcher";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { useCompany } from "@/context/CompanyContext";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/fichiers", label: "Mes fichiers", icon: FolderOpen, id: "files" },
  { to: "/partages", label: "Partagés avec moi", icon: Users, id: "shared" },
  { to: "/recherche", label: "Recherche", icon: Search, id: "search" },
  { to: "/assistant", label: "Assistant IA", icon: Bot, id: "assistant" },
  { to: "/validations", label: "Validations", icon: ClipboardCheck, id: "validations", badge: true },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Truck, id: "suppliers", hideFor: ["externe"] },
  { to: "/liens", label: "Liens de partage", icon: Link2, id: "links" },
  { to: "/corbeille", label: "Corbeille", icon: Trash2, id: "trash" },
  { to: "/compte", label: "Synchro & RGPD", icon: Laptop, id: "account" },
];

function NavList({ onNavigate }) {
  const { me } = useMe();
  const { refreshKey } = useFiles();
  const location = useLocation();
  const { current } = useCompany();
  const [todo, setTodo] = useState(0);
  useEffect(() => { api.get("/approvals/count").then((r) => setTodo(r.data.todo)).catch(() => {}); }, [refreshKey, location.pathname, current]);
  const visible = NAV.filter((n) => !n.hideFor?.includes(me.role));
  const items = me.role === "admin" ? [...visible, { to: "/admin", label: "Administration", icon: Shield, id: "admin" }] : visible;
  return (
    <nav className="space-y-0.5">
      {items.map(({ to, label, icon: I, id, end, badge }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${id}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            isActive ? "bg-[#1E3A8A] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
          <I className="h-4 w-4" />{label}
          {badge && todo > 0 && <span className="ml-auto text-[10px] font-bold bg-amber-600 text-white rounded-full px-1.5 min-w-5 text-center" data-testid="nav-validations-count">{todo}</span>}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarBody({ onNavigate }) {
  const { me } = useMe();
  const { company } = useCompany();
  return (
    <div className="h-full flex flex-col tread-bg p-4">
      <div className="px-2 py-2 mb-2"><Logo light /></div>
      <div className="mx-2 mb-5 flex items-center gap-2 text-xs text-slate-300" data-testid="sidebar-company">
        <span className="h-2 w-2 rounded-full" style={{ background: company?.color || "#64748b" }} />
        <span className="truncate">{company?.name}</span>
      </div>
      <NavList onNavigate={onNavigate} />
      <div className="mt-auto rounded-lg bg-white/5 border border-white/10 p-3" data-testid="sidebar-user">
        <div className="text-sm text-white font-medium truncate">{me.name}</div>
        <div className="text-xs text-slate-400 truncate">{me.email}</div>
        <span className="inline-block mt-2 text-[10px] uppercase tracking-wider font-semibold text-amber-400">{ROLE_LABELS[me.role]}</span>
      </div>
    </div>
  );
}

function Notifications() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const { refreshKey } = useFiles();
  const load = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [refreshKey]);
  const unread = items.filter((n) => !n.read).length;
  return (
    <Popover onOpenChange={(o) => { if (!o && unread) api.post("/notifications/read").then(load); }}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label="Notifications" data-testid="notifications-btn">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-amber-600 text-[10px] text-white flex items-center justify-center" data-testid="notifications-count">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b font-semibold text-sm">Notifications</div>
        <div className="max-h-80 overflow-auto">
          {items.length === 0 && <p className="p-4 text-sm text-slate-500">Aucune notification</p>}
          {items.map((n) => (
            <button key={n.id} onClick={() => n.link && navigate(n.link)} data-testid={`notification-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-slate-50 ${n.read ? "" : "bg-blue-50/60"}`}>
              <div className="text-sm">{n.message}</div>
              <div className="text-xs text-slate-500 mt-1">{fmtDate(n.created_at, true)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const { openFile } = useFiles();

  useEffect(() => {
    const f = params.get("file");
    if (f) { openFile(f); params.delete("file"); setParams(params, { replace: true }); }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (e) => { e.preventDefault(); if (q.trim()) navigate(`/recherche?q=${encodeURIComponent(q.trim())}`); };

  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0 z-30"><SidebarBody /></aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-72 border-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarBody onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 lg:pl-64 min-w-0 flex flex-col">
        <ImpersonationBanner />
        <header className="sticky top-0 z-20 h-16 bg-white border-b flex items-center gap-3 px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Menu" data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </Button>
          <CompanySwitcher />
          <form onSubmit={submit} className="flex-1 max-w-xl relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un document, une facture, un fournisseur…"
              className="pl-9 bg-slate-50 border-slate-200 h-10" data-testid="global-search-input" />
          </form>
          <div className="ml-auto flex items-center gap-2">
            <Notifications />
            <UserButton />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">{children}</main>
      </div>
    </div>
  );
}
